/**
 * The client-facing "Your Path" mastery map (LG-17).
 *
 * Pure derivation only: the router hands over rows, this decides what the
 * learner sees. Every rule here is the recommendation engine's own rule, not
 * a second opinion invented for the dashboard —
 *
 * - a subtopic counts as mastered at `classifyMasteryBucket(...) === 'mastered'`,
 *   the same bucket boundary the coach's proficiency-goals view reads;
 * - a topic rolls up through `rollUpSubtopicMastery`, the same function the
 *   coach's `rollUpTopicMastery` calls (the router hands over only the topics
 *   in this client's plan, which is the switch that wrapper adds);
 * - a prerequisite edge opens when the prerequisite topic's rollup reaches
 *   that edge's own `TopicPrerequisite.gateThreshold`.
 *
 * All three run on `effectiveMastery` (the ceiling decayed by FSRS
 * retrievability), never on the stored ceiling: the coach's views decay, and a
 * learner map that did not would show a topic mastered months after the recall
 * behind it has faded.
 */

import { classifyMasteryBucket } from '../recommendation/proficiency-range.js'
import {
  daysBetween,
  effectiveMastery,
} from '../recommendation/retrievability.js'
import { rollUpSubtopicMastery } from '../recommendation/topic-mastery-rollup.js'

/**
 * mastered    — every enabled subtopic at or above its mastery threshold
 * reinforcing — most subtopics there (>= 2/3); drilling the rest to lock it in
 * learning    — started, still early
 * available   — prerequisites open, nothing started yet
 * locked      — a prerequisite topic has not reached its gate
 */
export type MasteryTopicStatus =
  | 'mastered'
  | 'reinforcing'
  | 'learning'
  | 'available'
  | 'locked'

/** Fraction of a topic's subtopics that must be mastered to read as reinforcing. */
const REINFORCING_FRACTION = 2 / 3

export interface MasteryPathSubtopicInput {
  id: string
  slug: string
  name: string
  /** Slow-EMA ceiling, 0-100. Unchanged by time. */
  mastery: number
  /** Days-to-90%-recall for the FSRS decay applied to `mastery`. */
  stabilityDays: number
  /** Null for a subtopic the client has never attempted. */
  lastAttemptAt: string | null
  minProficiencyThreshold: number
  maxProficiencyThreshold: number
  rollupWeight: number
  isEnabled: boolean
}

export interface MasteryPathTopicInput {
  id: string
  slug: string
  name: string
  displayOrder: number
  subtopics: MasteryPathSubtopicInput[]
}

export interface MasteryPathPrerequisiteInput {
  topicId: string
  prerequisiteTopicId: string
  gateThreshold: number
}

export interface MasteryPathInput {
  topics: MasteryPathTopicInput[]
  prerequisites: MasteryPathPrerequisiteInput[]
  /** Read time for the decay — injected so the derivation stays pure. */
  now: Date
}

export interface MasteryPathSubtopic {
  id: string
  slug: string
  name: string
  /** `mastery` after FSRS decay, rounded to the whole points the UI shows. */
  effectiveMastery: number
  mastered: boolean
  /** True once the client has attempted it at all. */
  started: boolean
}

export interface MasteryPathTopic {
  id: string
  slug: string
  name: string
  status: MasteryTopicStatus
  /** Weighted rollup of the enabled subtopics, 0-100. */
  mastery: number
  masteredCount: number
  /** Enabled subtopics only — the denominator behind `masteredCount`. */
  totalCount: number
  subtopics: MasteryPathSubtopic[]
  /** Names of the prerequisite topics still short of their gate. */
  blockedBy: string[]
  /** Percent-based position within the graph canvas. */
  x: number
  y: number
}

export interface MasteryPathEdge {
  fromTopicId: string
  toTopicId: string
  /** The prerequisite has reached its gate, so this edge has been walked. */
  unlocked: boolean
}

export interface MasteryPath {
  topics: MasteryPathTopic[]
  edges: MasteryPathEdge[]
}

/**
 * A never-attempted subtopic falls out of `daysBetween` as zero elapsed days,
 * so its retrievability is 1 and its (typically seeded) ceiling passes through
 * undecayed — the same treatment the coach's snapshot gives it.
 */
function decayedMastery(subtopic: MasteryPathSubtopicInput, now: Date): number {
  return Math.round(
    effectiveMastery(
      subtopic.mastery,
      daysBetween(subtopic.lastAttemptAt, now),
      subtopic.stabilityDays,
    ),
  )
}

/**
 * Longest-path depth of every topic over the prerequisite DAG, which is the
 * row it draws in. Memoized, and cycle-tolerant through an on-stack guard: a
 * back edge counts as depth 0, so the walk terminates. The catalog is a DAG
 * today; a coach override or a bad import puts the cycle's topics on whatever
 * rows the walk's entry point implies — arbitrary, but the same on every read,
 * since both queries feeding this are totally ordered — rather than recursing
 * forever on a dashboard read.
 */
function depthByTopicId(
  topicIds: string[],
  prerequisiteIdsByTopicId: Map<string, string[]>,
): Map<string, number> {
  const depths = new Map<string, number>()
  const resolving = new Set<string>()

  const resolve = (topicId: string): number => {
    const cached = depths.get(topicId)
    if (cached !== undefined) return cached
    // Back edge: this topic is already on the stack below us, so treat the
    // cycle as if it started here rather than recursing forever.
    if (resolving.has(topicId)) return 0
    resolving.add(topicId)
    let depth = 0
    for (const prerequisiteId of prerequisiteIdsByTopicId.get(topicId) ?? []) {
      depth = Math.max(depth, resolve(prerequisiteId) + 1)
    }
    resolving.delete(topicId)
    depths.set(topicId, depth)
    return depth
  }

  for (const topicId of topicIds) resolve(topicId)
  return depths
}

/** Vertical band the rows spread across, in canvas percent. */
const FIRST_ROW_Y = 5
const LAST_ROW_Y = 93

function positionsByTopicId(
  topics: MasteryPathTopicInput[],
  depths: Map<string, number>,
): Map<string, { x: number; y: number }> {
  const rows = new Map<number, MasteryPathTopicInput[]>()
  for (const topic of topics) {
    const depth = depths.get(topic.id) ?? 0
    const row = rows.get(depth)
    if (row) row.push(topic)
    else rows.set(depth, [topic])
  }

  const maxDepth = Math.max(0, ...rows.keys())
  const positions = new Map<string, { x: number; y: number }>()
  for (const [depth, row] of rows) {
    // `displayOrder` is the catalog's learn-first order, so a row reads left to
    // right the way the curriculum does. `id` breaks the ties the catalog
    // leaves, keeping the layout identical across reloads.
    const ordered = [...row].sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        left.id.localeCompare(right.id),
    )
    const y =
      maxDepth === 0
        ? FIRST_ROW_Y
        : FIRST_ROW_Y + (depth * (LAST_ROW_Y - FIRST_ROW_Y)) / maxDepth
    ordered.forEach((topic, index) => {
      positions.set(topic.id, {
        x: ((index + 1) / (ordered.length + 1)) * 100,
        y,
      })
    })
  }
  return positions
}

/**
 * The five-state ladder, in one place. The live map classifies through it, and
 * so do the sample-data preview tracks in `mastery-data`, so a preview tab
 * cannot quietly grade the same shape of progress on a different rule than the
 * real one.
 */
export function masteryTopicStatus(input: {
  masteredCount: number
  /** Enabled subtopics only. */
  totalCount: number
  /** Touched at all — a topic is `learning` before anything is mastered. */
  started: boolean
  prerequisitesMet: boolean
}): MasteryTopicStatus {
  if (!input.prerequisitesMet) return 'locked'
  // A topic with no enabled subtopics has nothing to master; it is open to
  // start rather than silently complete.
  if (input.totalCount === 0) return 'available'
  if (input.masteredCount === input.totalCount) return 'mastered'
  if (input.masteredCount / input.totalCount >= REINFORCING_FRACTION) {
    return 'reinforcing'
  }
  return input.started ? 'learning' : 'available'
}

function statusFor(
  subtopics: MasteryPathSubtopic[],
  prerequisitesMet: boolean,
): MasteryTopicStatus {
  return masteryTopicStatus({
    masteredCount: subtopics.filter((st) => st.mastered).length,
    totalCount: subtopics.length,
    started: subtopics.some((st) => st.started || st.effectiveMastery > 0),
    prerequisitesMet,
  })
}

export function buildMasteryPath(input: MasteryPathInput): MasteryPath {
  const topicById = new Map(input.topics.map((topic) => [topic.id, topic]))
  // Edges pointing at or from a topic this read did not load (soft-deleted
  // between the two queries) describe nothing drawable — drop them here so
  // neither the layout nor the gate check has to keep re-checking.
  const prerequisites = input.prerequisites.filter(
    (edge) =>
      topicById.has(edge.topicId) && topicById.has(edge.prerequisiteTopicId),
  )

  const prerequisiteIdsByTopicId = new Map<string, string[]>()
  for (const edge of prerequisites) {
    const existing = prerequisiteIdsByTopicId.get(edge.topicId)
    if (existing) existing.push(edge.prerequisiteTopicId)
    else prerequisiteIdsByTopicId.set(edge.topicId, [edge.prerequisiteTopicId])
  }

  // Pass 1: per-topic subtopic views and rollups, which the gates then read.
  const subtopicsByTopicId = new Map<string, MasteryPathSubtopic[]>()
  const masteryByTopicId = new Map<string, number>()
  for (const topic of input.topics) {
    const enabled = topic.subtopics.filter((st) => st.isEnabled)
    const views = enabled.map((st) => {
      const decayed = decayedMastery(st, input.now)
      return {
        id: st.id,
        slug: st.slug,
        name: st.name,
        effectiveMastery: decayed,
        mastered:
          classifyMasteryBucket(
            decayed,
            st.minProficiencyThreshold,
            st.maxProficiencyThreshold,
          ) === 'mastered',
        started: st.lastAttemptAt !== null,
      }
    })
    subtopicsByTopicId.set(topic.id, views)
    const weightById = new Map(enabled.map((st) => [st.id, st.rollupWeight]))
    masteryByTopicId.set(
      topic.id,
      rollUpSubtopicMastery(
        views.map((view) => ({
          effectiveMastery: view.effectiveMastery,
          rollupWeight: weightById.get(view.id),
        })),
      ),
    )
  }

  const depths = depthByTopicId(
    input.topics.map((topic) => topic.id),
    prerequisiteIdsByTopicId,
  )
  const positions = positionsByTopicId(input.topics, depths)

  const gateMet = (edge: MasteryPathPrerequisiteInput): boolean =>
    (masteryByTopicId.get(edge.prerequisiteTopicId) ?? 0) >= edge.gateThreshold

  const topics = input.topics.map((topic): MasteryPathTopic => {
    const subtopics = subtopicsByTopicId.get(topic.id) ?? []
    const blockedBy = prerequisites
      .filter((edge) => edge.topicId === topic.id && !gateMet(edge))
      .flatMap((edge) => {
        const prerequisite = topicById.get(edge.prerequisiteTopicId)
        return prerequisite ? [prerequisite.name] : []
      })
      .sort()
    const position = positions.get(topic.id) ?? { x: 50, y: FIRST_ROW_Y }
    return {
      id: topic.id,
      slug: topic.slug,
      name: topic.name,
      status: statusFor(subtopics, blockedBy.length === 0),
      mastery: masteryByTopicId.get(topic.id) ?? 0,
      masteredCount: subtopics.filter((st) => st.mastered).length,
      totalCount: subtopics.length,
      subtopics,
      blockedBy,
      x: position.x,
      y: position.y,
    }
  })

  const edges = prerequisites.map(
    (edge): MasteryPathEdge => ({
      fromTopicId: edge.prerequisiteTopicId,
      toTopicId: edge.topicId,
      unlocked: gateMet(edge),
    }),
  )

  return { topics, edges }
}
